import { baseRouteId, isFerryRoute, isNjBusRoute, isRailRoute, isSubwayRoute } from '../../ui/colors'
import type { ServiceAlert } from '../../store/transit'

// Shared vocabulary for the alerts screen. A plain module because the page and its two
// sub-components all read from it, and a `.svelte` file can't export constants.

// One tab per mode, and no "All". An "All" tab reads as the safe default and so is where
// everyone lands, and there a hundred bus detours bury the one suspended train. Ferry is its
// own tab rather than riding in Bus: its alerts come from a different operator (NYC Ferry,
// not the MTA) and a rider looking for "is my boat running" should not wade through detours.
// LIRR + Metro-North share one Rail tab for the same reason: a commuter checking their train
// wants the railroads' notices, not the subway's. NJ Transit's buses get one too: a different
// agency, and its route numbers (1, 5, 10…) would read as subway lines or bury the MTA's
// own bus detours if they shared a tab.
export type Kind = 'subway' | 'bus' | 'ferry' | 'rail' | 'njbus'

/** The tab a route id belongs under. */
export function routeKind(routeId: string): Kind {
  return isSubwayRoute(routeId) ? 'subway'
    : isFerryRoute(routeId) ? 'ferry'
    : isRailRoute(routeId) ? 'rail'
    : isNjBusRoute(routeId) ? 'njbus'
    : 'bus'
}
export type When = 'now' | 'upcoming'

// The subway index, grouped as the system map colors it: one row per trunk line. That is how
// a rider already holds the network ("the orange lines"), and it turns 25 route ids into 11
// rows that fit a phone without scrolling.
//
// NOT derived from the hex values in Colors.subway: the SI shares the 8 Av blue and the
// shuttles share one grey, so folding by color would claim a relationship between lines that
// share nothing but a paint can.
export const SUBWAY_GROUPS: { name: string; lines: string[] }[] = [
  { name: 'Broadway–7 Av', lines: ['1', '2', '3'] },
  { name: 'Lexington Av', lines: ['4', '5', '6'] },
  { name: 'Flushing', lines: ['7'] },
  { name: '8 Av', lines: ['A', 'C', 'E'] },
  { name: '6 Av', lines: ['B', 'D', 'F', 'M'] },
  { name: 'Crosstown', lines: ['G'] },
  { name: 'Nassau St', lines: ['J', 'Z'] },
  { name: '14 St–Canarsie', lines: ['L'] },
  { name: 'Broadway', lines: ['N', 'Q', 'R', 'W'] },
  // All three shuttles in one row. They share a bullet, a color and a name on every sign in
  // the system, so splitting them by their internal ids (GS/FS/H) would show a rider three
  // rows they cannot tell apart anyway; which shuttle it is comes from the alert text.
  { name: 'Shuttles', lines: ['GS', 'FS', 'H'] },
  { name: 'Staten Island Railway', lines: ['SI'] },
]

// Shuttle route ids are internal names; every sign, map and rider calls all three "S".
export const LINE_LABEL: Record<string, string> = { GS: 'S', FS: 'S', H: 'S' }

// Which is which, for the three bullets that all read "S": hover text on desktop, and the
// only thing a screen reader has to go on.
export const LINE_TITLE: Record<string, string> = {
  GS: '42 St Shuttle',
  FS: 'Franklin Av Shuttle',
  H: 'Rockaway Park Shuttle',
}

/**
 * The id a route counts under in the index. Folds the express variants onto their line (the
 * 6X's alerts are the 6's, to everyone but NYCT) and the bare 'S' onto 'GS', which is the
 * same 42 St shuttle under whichever name the alert happened to arrive with.
 */
export function indexLine(routeId: string): string {
  const base = baseRouteId(routeId)
  return base === 'S' ? 'GS' : base
}

// Which tab an alert belongs to. A station-only notice (no routeIds at all) lands under
// Subway: MTA publishes them for subway stations, and with the "All" tab gone there is no
// neutral bucket left — dropping them would make a tab the rider reads as complete quietly
// incomplete.
export function belongsTo(alert: ServiceAlert, kind: Kind): boolean {
  if (kind === 'subway') return alert.routeIds.some(isSubwayRoute) || !alert.routeIds.some(Boolean)
  return alert.routeIds.some((r) => r && routeKind(r) === kind)
}

// `<input type="date">` speaks 'YYYY-MM-DD' in the viewer's local zone, and so must every
// conversion around it — using an ISO/UTC string here would land the filter on the wrong
// day for anyone west of GMT (i.e. all of NYC) after evening.
export function toDateInput(ms: number): string {
  const d = new Date(ms)
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${m}-${day}`
}

/** Local midnight (ms) for a 'YYYY-MM-DD' string. */
export function dateInputToMs(value: string): number {
  const [y, m, d] = value.split('-').map(Number)
  return new Date(y, m - 1, d).getTime()
}

// No weekday: the date chip shares a width with the Subway/Bus chips, and "Sat, Jul 12"
// does not survive a third of a phone's width at 12px.
export const CHIP_DATE_FORMAT = new Intl.DateTimeFormat(undefined, {
  month: 'short',
  day: 'numeric',
})

// A filter chip in the row above the list. Sized to its own label rather than to an even
// split: the redesign runs them left-to-right as a sentence ("Now · 126   Upcoming · 134")
// and pushes the date chip to the far right, which an equal-width grid can't express.
// Squared off at 8px — round pills are the map's floating chrome, not the panel's.
export const CHIP_BASE =
  'box-sizing: border-box;' +
  ' display: inline-flex; align-items: center; justify-content: center; gap: 5px;' +
  ' padding: 7px 12px; border-radius: 8px;' +
  ' font-size: var(--bjl-font-size-xs); font-weight: 700; line-height: 1.2;' +
  ' white-space: nowrap;'

/** One run of an alert's text: plain words, or a line MTA wrote as a bracketed bullet. */
export type AlertTextPart = { text: string } | { routeId: string }

// MTA writes a line's bullet into its alert prose as a bracketed id — "[E][F] trains are
// running with delays" — which its own site swaps for the icon. The bracket names the
// SIGN, not the GTFS id: the Staten Island Railway is "[SIR]" (route SI). Anything else in
// brackets ("[accessibility icon]", "[shuttle bus icon]") is left as written.
const ALERT_TOKEN = /\[([A-Za-z0-9]{1,8})\]/g
const TOKEN_ROUTE: Record<string, string> = { SIR: 'SI' }

/**
 * Splits alert text into plain runs and route bullets. A token becomes a bullet when it is a
 * subway line, or one of the alert's own routes by id or by the label its badge shows (how a
 * bus or ferry id would be written, should its operator ever bracket one).
 */
export function alertTextParts(text: string, routeIds: readonly string[] = []): AlertTextPart[] {
  const own = new Map<string, string>()
  for (const r of routeIds) {
    if (!r) continue
    own.set(r, r)
    const label = LINE_LABEL[r]
    if (label && !own.has(label)) own.set(label, r)
  }
  const parts: AlertTextPart[] = []
  let last = 0
  for (const m of text.matchAll(ALERT_TOKEN)) {
    const token = m[1]
    const routeId = own.get(token) ?? TOKEN_ROUTE[token] ?? (isSubwayRoute(token) ? token : undefined)
    if (!routeId) continue
    if (m.index > last) parts.push({ text: text.slice(last, m.index) })
    parts.push({ routeId })
    last = m.index + m[0].length
  }
  if (last < text.length) parts.push({ text: text.slice(last) })
  return parts
}
